import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ClipboardList, 
  Plus, 
  Search,
  Eye,
  Edit3,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Clock,
  FileText,
  GitBranch
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Procedure categories
const PROCEDURE_CATEGORIES = [
  'Safety',
  'Maintenance',
  'Operations',
  'Emergency',
  'Inspection',
  'Start-up',
  'Shutdown',
  'Other'
];

function ProceduresPanel({ isOpen, onClose, title = "Workflows" }) {
  const { token, canEdit, isViewer } = useAuth();
  const { currentTour } = useTour();
  
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  // Fetch procedures for current tour
  const fetchProcedures = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/procedures/tour/${currentTour.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setProcedures(data);
      }
    } catch (err) {
      console.error('Failed to fetch procedures:', err);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  useEffect(() => {
    if (isOpen) {
      fetchProcedures();
    }
  }, [isOpen, fetchProcedures]);

  // Filter procedures
  const filteredProcedures = procedures.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          p.procedure_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || p.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Status badge
  const StatusBadge = ({ status }) => {
    const config = {
      active: { label: 'Active', variant: 'success', icon: CheckCircle2 },
      draft: { label: 'Draft', variant: 'secondary', icon: Clock },
      review: { label: 'Under Review', variant: 'warning', icon: AlertCircle },
      archived: { label: 'Archived', variant: 'outline', icon: FileText },
    };
    const cfg = config[status] || config.draft;
    const Icon = cfg.icon;
    return (
      <Badge variant={cfg.variant} className="gap-1 text-[10px]">
        <Icon className="h-3 w-3" />
        {cfg.label}
      </Badge>
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-primary" />
            {title}
          </SheetTitle>
          <SheetDescription>
            Standard operating procedures and work instructions
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col h-[calc(100vh-180px)] mt-4">
          {/* Search and Add */}
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search procedures..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            {canEdit && (
              <Button size="sm" className="gap-1" disabled={!currentTour?.id}>
                <Plus className="h-3 w-3" />
                Add
              </Button>
            )}
          </div>

          {/* Category filter */}
          <div className="flex gap-1 mb-4 flex-wrap">
            <Button
              variant={filterCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setFilterCategory('all')}
            >
              All
            </Button>
            {PROCEDURE_CATEGORIES.slice(0, 4).map(cat => (
              <Button
                key={cat}
                variant={filterCategory === cat ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setFilterCategory(cat)}
              >
                {cat}
              </Button>
            ))}
          </div>

          {/* Viewer notice */}
          {isViewer && (
            <div className="text-xs text-muted-foreground bg-secondary/50 p-2 rounded mb-4">
              View-only access. Contact an admin to manage procedures.
            </div>
          )}

          {/* Procedures list */}
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : filteredProcedures.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <ClipboardList className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No procedures found</p>
                <p className="text-xs mt-1">Add procedures to document standard operations</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredProcedures.map(procedure => (
                  <div 
                    key={procedure.id}
                    className="p-3 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{procedure.name}</p>
                          <StatusBadge status={procedure.status} />
                        </div>
                        {procedure.procedure_number && (
                          <p className="text-xs text-muted-foreground mt-1">
                            #{procedure.procedure_number}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-[10px]">
                            {procedure.category || 'Other'}
                          </Badge>
                          {procedure.revision && (
                            <span className="text-xs text-muted-foreground">
                              Rev {procedure.revision}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon-sm" title="View">
                          <Eye className="h-3 w-3" />
                        </Button>
                        {canEdit && (
                          <>
                            <Button variant="ghost" size="icon-sm" title="Edit">
                              <Edit3 className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="icon-sm" title="Delete">
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ProceduresPanel;
